import React from "react";
import Header from "../../components/header";
import Footer from "../../components/footer";
import styles from "./index.module.css";
import marketImage from "../../images/about-redesign/market-scene.jpg";
import storyImage from "../../images/about-redesign/story-scene.jpg";
import Leadership from "./leadership";
import targetIcon from "../../images/about-redesign/target-arrow.svg";
import visionIcon from "../../images/about-redesign/vision-eye.svg";

const milestones = [
	{
		date: "2019·Origins",
		text: "Founded in Abatete to serve individuals and businesses the mainstream banks overlooked.",
	},
	{
		date: "2022·Digital",
		text: "Launched the Zikora App, bringing minutes-not-queues banking to thousands across the region.",
	},
	{
		date: "2026·Z-ForHer",
		text: "Introduced flexible credit built specifically for women entrepreneurs growing their hustle.",
	},
	{
		date: "Today",
		text: "Committed to individuals, businesses, and communities — banking that moves at the speed of local life.",
	},
];

const SectionLabel = ({ children, dot = true }) => (
	<div className={styles.label}>
		{dot && <span aria-hidden="true" />}
		{children}
	</div>
);

const SectionHeading = ({ label, children, intro, className = "" }) => (
	<div className={`${styles.sectionHeading} ${className}`}>
		<SectionLabel>{label}</SectionLabel>
		<h2>{children}</h2>
		{intro && <p>{intro}</p>}
	</div>
);

const About = () => (
	<main className={styles.page}>
		<div className={styles.headerWrap}>
			<Header solid />
		</div>

		<section className={styles.hero} style={{ "--hero-image": `url(${marketImage})` }}>
			<div className={styles.heroShade} />
			<div className={styles.heroContent}>
				<div>
					<SectionLabel>About Zikora</SectionLabel>
					<h1>
						Built in Anambra. Built for the people we <em>serve.</em>
					</h1>
					<p className={styles.heroIntro}>
						From our roots in Abatete to communities across Nigeria, Zikora MFB was founded to make banking more accessible, more personal, and more responsive to the people and businesses driving local growth.
					</p>
				</div>
				<div className={styles.stats}>
					<div><strong>2,400+</strong><span>SMEs banking with us</span></div>
					<div><strong>1</strong><span>Branch, plus 2 meeting points</span></div>
					<div><strong>₦3.2B</strong><span>Disbursed to local businesses</span></div>
				</div>
			</div>
		</section>

		<section className={styles.story}>
			<img className={styles.storyImage} src={storyImage} alt="A ledger at Zikora's local roots" loading="lazy" decoding="async" />
			<div className={styles.storyContent}>
				<SectionHeading label="How it started">A bank with local roots and a clear purpose.</SectionHeading>
				<div className={styles.storyCopy}>
					<p>Zikora began in Abatete, Anambra State, not in a boardroom, but in the markets, workshops, and shopfronts of Eastern Nigeria. We saw hardworking people and thriving businesses underserved by banking that felt distant, slow, and built for someone else.</p>
					<p>So we built a different kind of bank. One that speaks the language of local trade, understands the rhythm of the seasons, and treats every customer, from the petty trader to the manufacturer, as someone worth knowing by name.</p>
				</div>
				<div className={styles.timeline}>
					{milestones.map((milestone) => (
						<div className={styles.milestone} key={milestone.date}>
							<span>{milestone.date}</span>
							<p>{milestone.text}</p>
						</div>
					))}
				</div>
			</div>
		</section>

		<section className={styles.purpose}>
			<div className={styles.purposeInner}>
				<SectionHeading label="Purpose">Why we exist, and where we’re going.</SectionHeading>
				<div className={styles.purposeGrid}>
					<article className={styles.purposeCard}>
						<span className={styles.roundIcon} aria-hidden="true"><img src={targetIcon} alt="" /></span>
						<h3>Our Mission</h3>
						<p>To make banking accessible, personal, and genuinely useful for everyday Nigerians — putting the financial tools to save, borrow, and grow within reach of the people and businesses that power local economies.</p>
					</article>
					<article className={`${styles.purposeCard} ${styles.visionCard}`}>
						<span className={styles.roundIcon} aria-hidden="true"><img src={visionIcon} alt="" /></span>
						<h3>Our Vision</h3>
						<p>To become Eastern Nigeria’s most trusted financial partner — a bank so rooted in its communities that growth, dignity, and opportunity follow wherever Zikora goes.</p>
					</article>
				</div>
			</div>
		</section>

		<Leadership />

		<section className={styles.impact}>
			<div className={styles.sectionShell}>
				<SectionHeading label="Community Impact" intro="Banking is the means. Stronger communities are the point. Two initiatives at the heart of our work.">Investing in people, not just accounts.</SectionHeading>
				<div className={styles.impactGrid}>
					<article className={`${styles.impactCard} ${styles.womenCard}`}>
						<div><SectionLabel dot={false}>Women Empowerment</SectionLabel><h3>Nwanyi Bu Ike</h3><p>“A woman is strength.” Our flagship women’s initiative pairs flexible Z-ForHer credit with mentorship and business training, helping women turn small trades into lasting enterprises.</p></div>
						<div className={styles.impactStats}><div><strong>1,800+</strong><span>Women supported</span></div><div><strong>₦640M</strong><span>Credit extended</span></div></div>
					</article>
					<article className={`${styles.impactCard} ${styles.farmCard}`}>
						<div><SectionLabel dot={false}>Agriculture Empowerment</SectionLabel><h3>Oge Ugbo</h3><p>“The season of the farm.” A seasonal financing and savings programme built around the planting and harvest calendar, giving farmers fair access to inputs, storage, and working capital.</p></div>
						<div className={styles.impactStats}><div><strong>920+</strong><span>Farmers enrolled</span></div><div><strong>34</strong><span>Communities reached</span></div></div>
					</article>
				</div>
			</div>
		</section>

		<section className={styles.cta}>
			<SectionLabel>Join Zikora</SectionLabel>
			<h2>Join a bank that understands<br className={styles.desktopBreak} />{" "}where you’re coming from.</h2>
			<p>Whether you’re saving, borrowing, growing a business, or planning for the future, we’re here to help you move forward.</p>
			<div><a href="https://zikoramfb.qoreonline.com/onboarding/type">Open Account</a><a href="https://onelink.to/zikora">Download the App</a></div>
		</section>
		<div className={styles.footerWrap}><Footer about /></div>
	</main>
);

export default About;
